import { createContext, ReactNode, useState, useMemo, useContext } from 'react';

import type { AnswerFragment } from '../../present/activity-answers.graphql.generated';
import { ActivitiesContentType, LESSON_ACTIVITIES } from 'global/constants';

type ActivityAnswerPrint = {
  answers?: AnswerFragment[];
  classId: number;
  contentType: ActivitiesContentType;
  contentId: number;
  showAnswer: boolean;
  setAnswers: (answers: AnswerFragment[]) => void;
};

export const ActivityAnswerPrintContext = createContext<ActivityAnswerPrint>({
  answers: undefined,
  classId: 0,
  contentType: LESSON_ACTIVITIES,
  contentId: 0,
  showAnswer: true,
  setAnswers: () => {},
});

export const ActivityAnswerPrintContextProvider = ({
  children,
  classId,
  contentType,
  contentId,
  showAnswer = true,
}: {
  children: ReactNode;
  classId: number;
  contentType: ActivitiesContentType;
  contentId: number;
  showAnswer?: boolean;
}) => {
  const [answers, setAnswers] = useState<AnswerFragment[] | undefined>(
    undefined
  );

  const value = useMemo(
    () => ({
      answers,
      setAnswers,
    }),
    [answers, setAnswers]
  );

  return (
    <ActivityAnswerPrintContext.Provider
      value={{ ...value, classId, contentType, contentId, showAnswer }}
    >
      {children}
    </ActivityAnswerPrintContext.Provider>
  );
};

export const useActivityAnswersPrintContext = (componentId?: string) => {
  const { answers, setAnswers, classId, contentType, contentId, showAnswer } =
    useContext(ActivityAnswerPrintContext);
  return {
    answers: componentId
      ? answers?.filter(a => a.componentId === componentId) || []
      : answers || [],
    setAnswers,
    classId,
    contentType,
    contentId,
    showAnswer,
  };
};
